import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import type { ReactElement, ReactNode } from 'react'
import { Button } from '@/components/button/Button'
import { IconButton } from '@/components/icon-button/IconButton'
import { MenuContent } from '@/components/menu/MenuContent'

interface ActionBarActionBase {
  /** Icon shown in the button (and as sole content on mobile). */
  icon: ReactElement
  /** Label shown next to icon on desktop; used for aria-label when not provided. */
  label: string
  /** Override for screen readers when different from label (e.g. "Upload file"). */
  ariaLabel?: string
  /** Whether the action is disabled. */
  disabled?: boolean
}

/**
 * An action either runs a click handler or opens a dropdown menu — never both, so no one ships a
 * button that navigates and opens a menu at the same time.
 */
export type ActionBarAction =
  | (ActionBarActionBase & {
      /** Click handler. */
      onClick?: () => void
      menu?: never
    })
  | (ActionBarActionBase & {
      onClick?: never
      /** Dropdown menu contents (MenuItemGroup/MenuItem); the button becomes the menu trigger. */
      menu: ReactNode
    })

export interface ActionBarProps {
  /** Content on the left (e.g. breadcrumb, title, nav item). */
  content: ReactNode
  /** Action buttons on the right. On desktop: outlined primary with icon + label; on mobile: icon-only. */
  actions: ActionBarAction[]
  /** Optional content after the action buttons in the same header row. */
  trailing?: ReactNode
}

function ActionBarActionButton({ action }: { action: ActionBarAction }) {
  const ariaLabel = action.ariaLabel ?? action.label

  const desktopButton = (
    <Button
      variant="outlined"
      color="primary"
      size="sm"
      leadingIcon={action.icon}
      onClick={action.onClick}
      disabled={action.disabled}
      aria-label={ariaLabel}
      className="max-lg:hidden lg:inline-flex gap-1 !px-3"
    >
      {action.label}
    </Button>
  )

  const mobileButton = (
    <IconButton
      variant="outlined"
      size="sm"
      icon={action.icon}
      aria-label={ariaLabel}
      onClick={action.onClick}
      disabled={action.disabled}
      className="lg:hidden !p-1.5"
    />
  )

  // Each breakpoint variant needs its own Root/Trigger pair — Radix allows a single trigger per
  // root, so sharing one would leave the menu unopenable at the other breakpoint.
  if (action.menu) {
    return (
      <>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger disabled={action.disabled} asChild>
            {desktopButton}
          </DropdownMenu.Trigger>
          <MenuContent align="start" side="bottom">
            {action.menu}
          </MenuContent>
        </DropdownMenu.Root>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger disabled={action.disabled} asChild>
            {mobileButton}
          </DropdownMenu.Trigger>
          <MenuContent align="start" side="bottom">
            {action.menu}
          </MenuContent>
        </DropdownMenu.Root>
      </>
    )
  }

  return (
    <>
      {desktopButton}
      {mobileButton}
    </>
  )
}

/**
 * ActionBar — horizontal bar with content and action buttons.
 * Desktop (lg+): full buttons with icon + label. Below lg: icon-only outlined buttons.
 */
export function ActionBar({ content, actions, trailing }: ActionBarProps) {
  return (
    <div className="flex w-full min-w-0 items-center gap-4 px-4 py-3 md:px-0 lg:gap-3">
      <div className="min-w-0 flex-1">{content}</div>

      {/*
        Floors the row at 32px, so with `py-3` it settles at 56px for every content shorter than
        that — which today is all of them: breadcrumbs are 32px, a results header 24px, the source
        toggle ~30px. Without the floor the row followed its content and changed height between
        those states, and Matter Files swaps a 32px selection toolbar into this same slot, so a
        shorter state jumped the table under it (ENG-6936).

        This floors, it does not fix: content taller than 32px would grow the row and break the
        pairing with `ContextualActionBar`, which is a fixed 56px. Check that bar before putting
        anything taller in the content slot.
      */}
      <div className="flex min-h-8 shrink-0 items-center justify-end gap-2 lg:gap-3">
        {actions.map((action) => (
          <ActionBarActionButton key={action.label} action={action} />
        ))}
        {trailing}
      </div>
    </div>
  )
}

export default ActionBar
